"use client";

import Link from "next/link";
import { useState } from "react";

export function HowToBookContent({
  steps,
  promises,
  faqs,
}: {
  steps: { num: string; title: string; body: string }[];
  promises: { value: string; label: string }[];
  faqs: { q: string; a: string }[];
}) {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <section className="pbd-container pt-16 pb-2">
        <p className="m-0 text-sm text-[var(--muted)]">
          <Link href="/" className="text-[var(--muted)] no-underline hover:text-[var(--accent)]">
            Home
          </Link>{" "}
          <span className="opacity-50">/</span> <span className="text-[var(--accent)]">How to book</span>
        </p>
        <h1 className="mt-3.5 mb-0 font-[family-name:var(--font-display)] text-[clamp(2.4rem,5vw,4rem)] font-medium leading-[1.02] tracking-tight">
          How Booking Works
        </h1>
        <p className="mt-4 mb-0 max-w-[600px] text-[1.05rem] leading-[1.7] text-[var(--muted)]">
          Journey requests are reviewed by our trip team. No online payment is taken at the request stage.
        </p>
      </section>

      <section className="pbd-container py-12">
        <div className="grid gap-6 max-[980px]:grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => (
            <div
              key={s.num}
              className="rounded-lg border border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--surface)_45%,transparent)] p-7"
            >
              <span className="font-[family-name:var(--font-display)] text-[2rem] font-medium text-[var(--accent)]">{s.num}</span>
              <h3 className="mt-3 mb-0 font-[family-name:var(--font-display)] text-[1.25rem] font-medium">{s.title}</h3>
              <p className="mt-2.5 mb-0 text-sm leading-relaxed text-[var(--muted)]">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--surface)_40%,transparent)]">
        <div className="mx-auto grid max-w-[1280px] max-[980px]:grid-cols-2 lg:grid-cols-4">
          {promises.map((p) => (
            <div key={p.label} className="border-r border-[var(--border-subtle)] px-6 py-9 last:border-r-0">
              <p className="m-0 font-[family-name:var(--font-display)] text-[2.2rem] leading-none font-medium text-[var(--accent)]">
                {p.value}
              </p>
              <p className="mt-2.5 mb-0 text-[13px] text-[var(--muted)]">{p.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pbd-container py-20">
        <h2 className="mb-6 font-[family-name:var(--font-display)] text-[clamp(1.7rem,3vw,2.4rem)] font-medium tracking-tight">
          Common questions
        </h2>
        <div className="flex flex-col gap-2">
          {faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <div
                key={f.q}
                className={`overflow-hidden rounded-lg border border-[var(--border-subtle)] ${open ? "bg-[color-mix(in_srgb,var(--accent)_6%,transparent)]" : "bg-[color-mix(in_srgb,var(--surface)_45%,transparent)]"}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 px-6 py-5 text-left font-[inherit]"
                >
                  <span className="font-[family-name:var(--font-display)] text-[1.15rem] font-medium text-[var(--foreground)]">
                    {f.q}
                  </span>
                  <span
                    className="text-xl text-[var(--accent)] transition-transform duration-200"
                    style={{ transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
                  >
                    +
                  </span>
                </button>
                {/* Always in the page, hidden until opened (FAQPage data). */}
                <p hidden={!open} className="m-0 px-6 pb-5 text-sm leading-[1.75] text-[var(--muted)]">
                  {f.a}
                </p>
              </div>
            );
          })}
        </div>
        <div className="mt-12 text-center">
          <Link href="/plan" className="pbd-btn-primary inline-flex min-h-12 px-6">
            Start planning
          </Link>
        </div>
      </section>
    </>
  );
}
